import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, FolderOpen, MessageSquare, LogOut, Phone, Briefcase, AlarmClock, Archive, HelpCircle, FileCheck2, Settings, UserCog } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useMatter } from '../hooks/useMatter';
import ImpersonationBanner from './ImpersonationBanner';
import NoSubmissionBanner from './NoSubmissionBanner';
import FaqAssistant from './FaqAssistant';
import { GuidanceProvider } from '../hooks/useGuidance';

/**
 * BUG-5: resolve the identity shown in the dark header. While staff are
 * previewing as a client (impersonating), the header must read the previewed
 * person's name, not the staff user's. The orange ImpersonationBanner + its
 * Exit preview control remain the staff affordance.
 */
export function resolveHeaderIdentity(
  impersonating: boolean,
  impersonatedName: { given_names: string | null; family_name: string | null } | null,
  staffContact: { given_names: string | null; family_name: string | null } | null,
  email: string | undefined,
): { first: string; last: string } {
  if (impersonating) {
    return {
      first: impersonatedName?.given_names ?? '',
      last: impersonatedName?.family_name ?? '',
    };
  }
  return {
    first: staffContact?.given_names ?? email?.split('@')[0] ?? '',
    last: staffContact?.family_name ?? '',
  };
}

export default function PortalLayout() {
  const { user, signOut } = useAuth();
  const { contact, activeMatter, caseManager, isStaff, isOwner, actuallyStaff, impersonatedMatterId, impersonatedRole } = useMatter();
  const navigate = useNavigate();

  // While impersonating, fetch the previewed contact's name so the header
  // identity reflects the client being previewed, not the staff user. Mirrors
  // the read-only lookup ImpersonationBanner already performs. Name only; no
  // write path touched.
  const [impersonatedName, setImpersonatedName] = useState<{
    given_names: string | null;
    family_name: string | null;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!actuallyStaff || !impersonatedMatterId) {
      setImpersonatedName(null);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('matters')
        .select(
          `id,
           primary_contact:contacts!matters_primary_contact_id_fkey (given_names, family_name),
           sponsor_contact:contacts!matters_sponsor_contact_id_fkey (given_names, family_name)`,
        )
        .eq('id', impersonatedMatterId)
        .maybeSingle();
      if (cancelled || !data) return;
      const row = data as {
        primary_contact: { given_names: string | null; family_name: string | null } | null;
        sponsor_contact: { given_names: string | null; family_name: string | null } | null;
      };
      setImpersonatedName(impersonatedRole === 'sponsor' ? row.sponsor_contact : row.primary_contact);
    })();
    return () => {
      cancelled = true;
    };
  }, [actuallyStaff, impersonatedMatterId, impersonatedRole]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // Sponsors get their own Documents tab now (Wave 4). The Documents page
  // detects sponsor mode and renders the sponsor-side checklist + upload
  // pipeline scoped to /sponsor/<contact_id>/... in storage. This was hidden
  // entirely before, but sponsors need to upload Form 40SP, sponsor passport,
  // sponsor police checks, and sponsor Form 80 themselves.
  const isSponsor = impersonatedMatterId
    ? impersonatedRole === 'sponsor'
    : contact?.contact_type === 'sponsor';

  // Admin-only entries are filtered in below. Approved PDFs is built in
  // parallel by another agent (workstream B) and is admin-only — gate it on
  // staff_role='admin' so non-admin staff don't see a dead link.
  const isAdmin = contact?.staff_role === 'admin';
  const nav = isStaff
    ? [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/matters', label: 'Matters', icon: Briefcase },
        { to: '/deadlines', label: 'Deadlines', icon: AlarmClock },
        { to: '/admin/forms-review', label: 'Forms catalogue', icon: FileText },
        ...(isAdmin
          ? [{ to: '/admin/approved-pdfs', label: 'Approved PDFs', icon: FileCheck2 }]
          : []),
        { to: '/admin/faq-review', label: 'FAQ review', icon: HelpCircle },
        // Owner-only (Eli + Tzvi): the engagement-letter gate toggle lives here.
        ...(isOwner
          ? [{ to: '/admin/settings', label: 'Settings', icon: Settings }]
          : []),
        { to: '/matters/archived', label: 'Approved matters', icon: Archive },
        { to: '/account', label: 'Account settings', icon: UserCog },
      ]
    : isSponsor
    ? [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/intake', label: 'Intake Form', icon: FileText },
        { to: '/documents', label: 'Documents', icon: FolderOpen },
        { to: '/messages', label: 'Contact', icon: MessageSquare },
        { to: '/account', label: 'Account settings', icon: UserCog },
      ]
    : [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/intake', label: 'Intake Form', icon: FileText },
        // Form 80 is applicant-only, partner visas only: a separate,
        // pre-filled supplementary form with its own progress (Tzvi 2026-07-21).
        // 461 stays off until f80Prefill carries a 147 source map.
        ...(activeMatter && ['820', '801', '309', '100', '300'].includes(activeMatter.visa_subclass)
          ? [{ to: '/form-80', label: 'Form 80', icon: FileCheck2 }]
          : []),
        { to: '/documents', label: 'Documents', icon: FolderOpen },
        { to: '/messages', label: 'Contact', icon: MessageSquare },
        { to: '/account', label: 'Account settings', icon: UserCog },
      ];

  const { first: displayFirst, last: displayLast } = resolveHeaderIdentity(
    Boolean(impersonatedMatterId),
    impersonatedName,
    contact,
    user?.email ?? undefined,
  );

  return (
    <GuidanceProvider>
    <div className="min-h-screen bg-slate-50">
      <ImpersonationBanner />
      <NoSubmissionBanner />
      <header className="bg-[#0B2341] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img
                src="https://adiimmigration.com.au/wp-content/uploads/2021/06/ADI_Immigration-white.png"
                alt="ADI Immigration"
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              {caseManager?.phone && (
                <a
                  href={`tel:${caseManager.phone}`}
                  className="hidden sm:flex items-center gap-2 text-sm text-slate-200 hover:text-white"
                >
                  <Phone size={14} />
                  {caseManager.phone}
                </a>
              )}
              <div className="h-6 w-px bg-slate-700 hidden sm:block" />
              <div className="text-right text-xs hidden sm:block">
                <div className="font-medium">
                  {displayFirst} {displayLast}
                </div>
                {activeMatter?.matter_id && (
                  <div className="text-slate-400">{activeMatter.matter_id}</div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <nav className="card p-3 h-fit overflow-x-auto">
            <ul className="flex lg:flex-col gap-1 min-w-max lg:min-w-0">
              {nav.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive
                          ? 'bg-[#EBF7FD] text-[#1A8DC7]'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`
                    }
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <main>
            <Outlet />
          </main>
        </div>
      </div>

      <footer className="mt-12 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-slate-500 flex flex-col sm:flex-row gap-2 justify-between items-center">
          <div>
            ADI Immigration · A service of Partner Visa Australia Pty Ltd · ABN 97 670 826 892
            <br />
            Eli Bekker, Registered Migration Agent · MARN 1465541
          </div>
          <div className="text-center sm:text-right">
            <div>1300 729 331 · adiimmigration.com.au</div>
            <div className="mt-1 space-x-2">
              <a
                href="https://adiimmigration.com.au/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                Privacy Policy
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://www.mara.gov.au/tools-for-registered-agents/code-of-conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                MARA Code of Conduct
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Client-facing assistant. Shown in client mode only (real clients, or
          staff impersonating a client); hidden in staff working views. */}
      {!isStaff && <FaqAssistant />}
    </div>
    </GuidanceProvider>
  );
}
